import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess } from '@/types/api';
import type { InstituteNoticeList } from '@/types/instituteNotice';
import type { LeaveRequestForm } from '@/lib/leaveRequest.schema';
import type { StudentLeaveRequest } from '@/types/leave';
import type { ParentAttendanceMonthData } from '../types/attendance';
import type { ParentFeeAccount } from '../types/fees';
import type { ParentOverview } from '../types/overview';
import type { ParentCalendar } from '../types/calendar';
import type { ParentNoticeList } from '../types/notices';
import type { ParentLectureList } from '../types/lectures';
import type { ParentMessage, ParentMessagePage } from '../types/messages';
import type { ParentChild, ParentHomeworkList, ParentTaskList } from '../types/parent';
import type { ParentChildProfile } from '../types/profile';
import type { ParentResults, ParentUpcomingExams } from '../types/results';
import type { ParentTimetable } from '../types/timetable';

/**
 * Every call the parent panel makes.
 *
 * Its own area rather than a corner of the institute's: a parent is not staff,
 * and none of `/institute/*` is open to their role. The only id any path
 * carries is which of *their own* children the screen is about — the server
 * resolves it against the caller's guardian links, so a uuid belonging to
 * another family is a 404 (CLAUDE.md §10).
 */
export const parentApi = {
  /** The children this account is responsible for — the panel's switcher. */
  children: () => unwrap(apiClient.get<ApiSuccess<ParentChild[]>>('/parent/children')),

  /** Private, like every upload — needs the access token, so use SecureImage. */
  childPhotoPath: (childUuid: string) => `/parent/children/${childUuid}/photo`,

  /**
   * One child's register for one month.
   *
   * `month` is optional: without it the server answers with the latest month of
   * the session, which is what a parent opening the screen wants. The response
   * always says which month it answered for, so the screen follows rather than
   * having to work out what the session's last month was.
   */
  attendance: (childUuid: string, month?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ParentAttendanceMonthData>>(
        `/parent/children/${childUuid}/attendance`,
        { params: month ? { month } : undefined },
      ),
    ),

  /**
   * The work set for one child's section, with that child's own submission
   * against each piece.
   *
   * No filter, page or sort in the request: a term of work is a short list, the
   * server caps it, and every control on the screen narrows what is already in
   * the browser. A round trip per chip would be slower than the filtering it
   * replaced.
   */
  homework: (childUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ParentHomeworkList>>(`/parent/children/${childUuid}/homework`),
    ),

  /**
   * The projects and practicals set for the same section — the Tasks tab.
   *
   * Its own call rather than a parameter on the one above: the two read
   * different tables and come back in different shapes.
   */
  tasks: (childUuid: string) =>
    unwrap(apiClient.get<ApiSuccess<ParentTaskList>>(`/parent/children/${childUuid}/tasks`)),

  /**
   * Where the files on a piece of work are fetched from — private, so through
   * `downloadPrivateFile` with the access token. `worksheet`/`guidelines` is the
   * sheet the teacher handed out; `attachment` is the file the child handed in.
   */
  worksheetPath: (childUuid: string, homeworkUuid: string) =>
    `/parent/children/${childUuid}/homework/${homeworkUuid}/worksheet`,
  homeworkSubmissionPath: (childUuid: string, homeworkUuid: string) =>
    `/parent/children/${childUuid}/homework/${homeworkUuid}/attachment`,
  taskGuidelinesPath: (childUuid: string, activityUuid: string) =>
    `/parent/children/${childUuid}/tasks/${activityUuid}/guidelines`,
  taskSubmissionPath: (childUuid: string, activityUuid: string) =>
    `/parent/children/${childUuid}/tasks/${activityUuid}/attachment`,

  /** Every leave request ever filed for one child, newest first. */
  leaveRequests: (childUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<StudentLeaveRequest[]>>(
        `/parent/children/${childUuid}/leave-requests`,
      ),
    ),

  /** Files a new one. It lands in that child's class teacher's queue. */
  requestLeave: (childUuid: string, payload: LeaveRequestForm) =>
    unwrap(
      apiClient.post<ApiSuccess<StudentLeaveRequest>>(
        `/parent/children/${childUuid}/leave-requests`,
        payload,
      ),
    ),

  /** Withdraws one the class teacher has not answered yet. */
  cancelLeave: (childUuid: string, requestUuid: string) =>
    unwrap(
      apiClient.delete<ApiSuccess<StudentLeaveRequest>>(
        `/parent/children/${childUuid}/leave-requests/${requestUuid}`,
      ),
    ),

  /**
   * One week of the child's timetable, breaks included. `weekOf` is any day in
   * the week; omitted, the server answers for the week we are in.
   */
  timetable: (childUuid: string, weekOf?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ParentTimetable>>(`/parent/children/${childUuid}/timetable`, {
        params: weekOf ? { weekOf } : undefined,
      }),
    ),

  /** Every paper one child has been marked on this session, with their standing. */
  results: (childUuid: string) =>
    unwrap(apiClient.get<ApiSuccess<ParentResults>>(`/parent/children/${childUuid}/results`)),

  /** The published papers one child is still to sit, earliest first. */
  upcomingExams: (childUuid: string) =>
    unwrap(apiClient.get<ApiSuccess<ParentUpcomingExams>>(`/parent/children/${childUuid}/exams`)),

  /** What one child's teachers have told the family — their "Parents" and "Both" notices. */
  notices: (childUuid: string) =>
    unwrap(apiClient.get<ApiSuccess<ParentNoticeList>>(`/parent/children/${childUuid}/notices`)),

  /**
   * The office's board, as a parent is told it. Not per child: every child on
   * the account is at the same institute, so it is one board.
   */
  noticeBoard: () =>
    unwrap(apiClient.get<ApiSuccess<InstituteNoticeList>>('/parent/notice-board')),

  /**
   * The lessons recorded for one child's section — read-only. Uploaded videos
   * come back as short-lived signed URLs, minted for this request.
   */
  lectures: (childUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ParentLectureList>>(`/parent/children/${childUuid}/lectures`),
    ),

  /**
   * This session's academic calendar for one child's family — narrowed by the
   * server to entries addressed to parents and to that child's stage.
   */
  calendar: (childUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ParentCalendar>>(`/parent/children/${childUuid}/calendar`),
    ),

  /**
   * One page of the SMS and WhatsApp messages the office has sent this child's
   * family, newest first. Paged on the server — a family's history grows for as
   * long as the child is at the school.
   */
  messages: async (childUuid: string, page: number, limit: number): Promise<ParentMessagePage> => {
    const response = await apiClient.get<
      ApiSuccess<{ child: ParentChild; items: ParentMessage[] }>
    >(`/parent/children/${childUuid}/messages`, { params: { page, limit } });

    const { child, items } = response.data.data;
    return {
      child,
      items,
      meta: response.data.meta ?? { page, limit, total: items.length, totalPages: 1 },
    };
  },

  /**
   * One child's fee account this session and every receipt against it — the
   * office's own ledger, so it always agrees with the counter.
   */
  fees: (childUuid: string) =>
    unwrap(apiClient.get<ApiSuccess<ParentFeeAccount>>(`/parent/children/${childUuid}/fees`)),

  /**
   * One child's record as the office holds it. Every guardian's contacts but
   * the caller's own come back masked.
   */
  profile: (childUuid: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ParentChildProfile>>(`/parent/children/${childUuid}/profile`),
    ),

  /**
   * The dashboard: one child's day on one date, and the figures around it.
   * Without a date the server answers for its own today, in the institute's
   * timezone rather than the device's.
   */
  overview: (childUuid: string, date?: string) =>
    unwrap(
      apiClient.get<ApiSuccess<ParentOverview>>(`/parent/children/${childUuid}/overview`, {
        params: date ? { date } : undefined,
      }),
    ),
};
